/**
 * @fileoverview Chart context for managing ISCRI chart state and interactions
 * Updated to work with new /iscri/components endpoint
 * @author ISCRI Team
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
} from "react";

/**
 * Action types for chart reducer
 */
const ActionTypes = {
  TOGGLE_COMPONENT: "TOGGLE_COMPONENT",
  SET_NORMALIZATION: "SET_NORMALIZATION",
  ADD_COUNTRY_PAIR: "ADD_COUNTRY_PAIR",
  REMOVE_COUNTRY_PAIR: "REMOVE_COUNTRY_PAIR",
  CLEAR_COUNTRY_PAIRS: "CLEAR_COUNTRY_PAIRS",
  SET_CHART_DATA: "SET_CHART_DATA",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
  RESET_CHART: "RESET_CHART",
  //
  SET_AGGREGATION_MODE: "SET_AGGREGATION_MODE",
  ADD_SELECTED_COUNTRY: "ADD_SELECTED_COUNTRY",
  REMOVE_SELECTED_COUNTRY: "REMOVE_SELECTED_COUNTRY",
  CLEAR_SELECTED_COUNTRIES: "CLEAR_SELECTED_COUNTRIES",
};

/**
 * Initial state for chart context
 * Updated for new /iscri/components endpoint requirements
 */
const initialState = {
  selectedComponents: {
    total: true, // ISCRI total (iscri field)
    verbal: false, // ISCRI verbal (iscri3 field)
    material: false, // ISCRI material (iscri4 field)
    goldstein: false, // Goldstein ISCRI (iscrig field)
  },

  normalization: "none", // Options: 'none', 'baseline' (maps to API true/false)

  // Country pairs for comparison
  countryPairs: [],
  chartData: null,

  loading: false,
  error: null,

  aggregationMode: "pairs", // 'pairs' | 'global' | 'country'
  selectedCountries: [],
};

/**
 * Reducer for chart state management
 */
function chartReducer(state, action) {
  switch (action.type) {
    case ActionTypes.TOGGLE_COMPONENT: {
      const currentComponents = state.selectedComponents;
      const componentToToggle = action.payload;
      const enabledComponents =
        Object.values(currentComponents).filter(Boolean).length;

      // Don't allow disabling the last enabled component
      if (enabledComponents === 1 && currentComponents[componentToToggle]) {
        return state;
      }

      return {
        ...state,
        selectedComponents: {
          ...state.selectedComponents,
          [componentToToggle]: !state.selectedComponents[componentToToggle],
        },
      };
    }

    case ActionTypes.SET_NORMALIZATION:
      return {
        ...state,
        normalization: action.payload,
        chartData: null,
      };

    case ActionTypes.ADD_COUNTRY_PAIR: {
      const existingPairIndex = state.countryPairs.findIndex(
        (pair) =>
          pair.actor1Code === action.payload.actor1Code &&
          pair.actor2Code === action.payload.actor2Code
      );

      if (existingPairIndex >= 0) {
        const updatedPairs = [...state.countryPairs];
        updatedPairs[existingPairIndex] = {
          ...updatedPairs[existingPairIndex],
          ...action.payload,
        };
        return {
          ...state,
          countryPairs: updatedPairs,
        };
      }

      return {
        ...state,
        countryPairs: [...state.countryPairs, action.payload],
      };
    }

    case ActionTypes.REMOVE_COUNTRY_PAIR:
      return {
        ...state,
        countryPairs: state.countryPairs.filter(
          (_, index) => index !== action.payload
        ),
      };

    case ActionTypes.CLEAR_COUNTRY_PAIRS:
      return {
        ...state,
        countryPairs: [],
        chartData: null,
        error: null,
      };

    case ActionTypes.SET_CHART_DATA:
      return {
        ...state,
        chartData: action.payload,
        loading: false,
        error: null,
      };

    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    case ActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case ActionTypes.RESET_CHART:
      return {
        ...initialState,
      };

    case ActionTypes.SET_AGGREGATION_MODE:
      return {
        ...state,
        aggregationMode: action.payload,
        chartData: null,
      };

    case ActionTypes.ADD_SELECTED_COUNTRY: {
      const countryCode = action.payload.toUpperCase();

      if (state.selectedCountries.includes(countryCode)) {
        return state;
      }

      if (state.selectedCountries.length >= 2) {
        return state;
      }

      return {
        ...state,
        selectedCountries: [...state.selectedCountries, countryCode],
        chartData: null,
      };
    }

    case ActionTypes.REMOVE_SELECTED_COUNTRY: {
      const countryCode = action.payload.toUpperCase();
      return {
        ...state,
        selectedCountries: state.selectedCountries.filter(
          (code) => code !== countryCode
        ),
        chartData: null,
      };
    }

    case ActionTypes.CLEAR_SELECTED_COUNTRIES:
      return {
        ...state,
        selectedCountries: [],
        chartData: null,
      };

    default:
      return state;
  }
}

/**
 * Handle transition logic between aggregation modes
 * Manages data compatibility when switching modes
 *
 * @param {string} currentMode - Current aggregation mode
 * @param {string} newMode - New aggregation mode to transition to
 * @param {Object} state - Current state
 * @param {Function} dispatch - Dispatch function
 * @private
 */
function handleModeTransition(currentMode, newMode, state, dispatch) {
  if (currentMode === newMode) {
    dispatch({ type: ActionTypes.SET_AGGREGATION_MODE, payload: newMode });
    return;
  }

  if (currentMode === "pairs" && newMode === "global") {
    dispatch({ type: ActionTypes.CLEAR_COUNTRY_PAIRS });
    dispatch({ type: ActionTypes.SET_AGGREGATION_MODE, payload: newMode });
    return;
  }

  if (currentMode === "pairs" && newMode === "country") {
    const uniqueCountries = new Set();
    state.countryPairs.forEach((pair) => {
      if (pair.actor1Code) uniqueCountries.add(pair.actor1Code);
      if (pair.actor2Code) uniqueCountries.add(pair.actor2Code);
    });

    const countriesToKeep = Array.from(uniqueCountries).slice(0, 2);

    dispatch({ type: ActionTypes.CLEAR_COUNTRY_PAIRS });
    dispatch({ type: ActionTypes.CLEAR_SELECTED_COUNTRIES });
    countriesToKeep.forEach((code) => {
      dispatch({ type: ActionTypes.ADD_SELECTED_COUNTRY, payload: code });
    });
    dispatch({ type: ActionTypes.SET_AGGREGATION_MODE, payload: newMode });
    return;
  }

  if (currentMode === "global" && newMode === "pairs") {
    dispatch({ type: ActionTypes.SET_AGGREGATION_MODE, payload: newMode });
    return;
  }

  if (currentMode === "global" && newMode === "country") {
    dispatch({ type: ActionTypes.SET_AGGREGATION_MODE, payload: newMode });
    return;
  }

  if (currentMode === "country" && newMode === "pairs") {
    if (state.selectedCountries.length === 2) {
      const [actor1Code, actor2Code] = state.selectedCountries;

      const pair = {
        actor1Code,
        actor2Code,
        id: `${actor1Code}-${actor2Code}`,
        data: null,
      };

      dispatch({ type: ActionTypes.CLEAR_SELECTED_COUNTRIES });
      dispatch({ type: ActionTypes.ADD_COUNTRY_PAIR, payload: pair });
    } else {
      dispatch({ type: ActionTypes.CLEAR_SELECTED_COUNTRIES });
    }
    dispatch({ type: ActionTypes.SET_AGGREGATION_MODE, payload: newMode });
    return;
  }

  if (currentMode === "country" && newMode === "global") {
    dispatch({ type: ActionTypes.CLEAR_SELECTED_COUNTRIES });
    dispatch({ type: ActionTypes.SET_AGGREGATION_MODE, payload: newMode });
    return;
  }

  dispatch({ type: ActionTypes.SET_AGGREGATION_MODE, payload: newMode });
}

/**
 * Chart context
 */
const ChartContext = createContext(null);

/**
 * Chart context provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export function ChartProvider({ children }) {
  const [state, dispatch] = useReducer(chartReducer, initialState);

  /**
   * Toggle ISCRI component visibility (total, verbal, material)
   * @param {string} component - Component name
   */
  const toggleComponent = useCallback((component) => {
    dispatch({ type: ActionTypes.TOGGLE_COMPONENT, payload: component });
  }, []);

  /**
   * Set normalization type
   * Converts between UI format ('none'/'baseline') and API format (false/true)
   * @param {string} normalization - Normalization type ('none' or 'baseline')
   */
  const setNormalization = useCallback((normalization) => {
    dispatch({ type: ActionTypes.SET_NORMALIZATION, payload: normalization });
  }, []);

  /**
   * Add country pair to chart with validation
   * @param {Object} pair - Country pair object
   */
  const addCountryPair = useCallback((pair) => {
    // Validate pair before adding
    if (!pair.actor1Code || !pair.actor2Code) {
      throw new Error("Both actor1Code and actor2Code are required");
    }

    if (pair.actor1Code === pair.actor2Code) {
      throw new Error("Cannot analyze relations between the same country");
    }

    dispatch({ type: ActionTypes.ADD_COUNTRY_PAIR, payload: pair });
  }, []);

  /**
   * Remove country pair from chart by index
   * @param {number} index - Index of pair to remove
   */
  const removeCountryPair = useCallback((index) => {
    dispatch({ type: ActionTypes.REMOVE_COUNTRY_PAIR, payload: index });
  }, []);

  /**
   * Clear all country pairs
   */
  const clearCountryPairs = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_COUNTRY_PAIRS });
  }, []);

  /**
   * Set chart data - stable reference with useCallback
   * @param {Object} data - Chart data object
   */
  const setChartData = useCallback((data) => {
    dispatch({ type: ActionTypes.SET_CHART_DATA, payload: data });
  }, []);

  /**
   * Set loading state
   * @param {boolean} loading - Loading state
   */
  const setLoading = useCallback((loading) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: loading });
  }, []);

  /**
   * Set error state
   * @param {string|null} error - Error message
   */
  const setError = useCallback((error) => {
    dispatch({ type: ActionTypes.SET_ERROR, payload: error });
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_ERROR });
  }, []);

  /**
   * Set aggregation mode (pairs, global, country)
   * Handles data transition logic between modes
   * @param {string} mode - New aggregation mode
   */
  const setAggregationMode = useCallback(
    (mode) => {
      const validModes = ["pairs", "global", "country"];
      if (!validModes.includes(mode)) {
        console.error(`Invalid aggregation mode: ${mode}`);
        return;
      }

      handleModeTransition(state.aggregationMode, mode, state, dispatch);
    },
    [state]
  );

  /**
   * Add a country to selected countries (max 2)
   * @param {string} countryCode - ISO3 country code
   */
  const addSelectedCountry = useCallback((countryCode) => {
    if (!countryCode || typeof countryCode !== "string") {
      console.error("Invalid country code");
      return;
    }

    dispatch({ type: ActionTypes.ADD_SELECTED_COUNTRY, payload: countryCode });
  }, []);

  /**
   * Remove a country from selected countries
   * @param {string} countryCode - ISO3 country code
   */
  const removeSelectedCountry = useCallback((countryCode) => {
    if (!countryCode || typeof countryCode !== "string") {
      console.error("Invalid country code");
      return;
    }

    dispatch({
      type: ActionTypes.REMOVE_SELECTED_COUNTRY,
      payload: countryCode,
    });
  }, []);

  /**
   * Clear all selected countries
   */
  const clearSelectedCountries = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_SELECTED_COUNTRIES });
  }, []);

  /**
   * Reset chart to initial state
   */
  const resetChart = useCallback(() => {
    dispatch({ type: ActionTypes.RESET_CHART });
  }, []);

  /**
   * Get validation status for current state
   */
  const getValidationStatus = useMemo(() => {
    const hasSelectedComponents = Object.values(state.selectedComponents).some(
      Boolean
    );
    const hasCountryPairs = state.countryPairs.length > 0;
    const hasValidPairs = state.countryPairs.every(
      (pair) =>
        pair.actor1Code &&
        pair.actor2Code &&
        pair.actor1Code !== pair.actor2Code
    );

    return {
      hasSelectedComponents,
      hasCountryPairs,
      hasValidPairs,
      canGenerateChart:
        hasSelectedComponents && hasCountryPairs && hasValidPairs,
    };
  }, [state.selectedComponents, state.countryPairs]);

  /**
   * Convert normalization for API calls
   * Maps UI format to API format
   */
  const getNormalizationForAPI = useMemo(() => {
    return (
      state.normalization === "baseline" || state.normalization === "base_100"
    );
  }, [state.normalization]);

  /**
   * Get component statistics
   */
  const getComponentStats = useMemo(() => {
    const total = Object.keys(state.selectedComponents).length;
    const selected = Object.values(state.selectedComponents).filter(
      Boolean
    ).length;

    return {
      total,
      selected,
      available: ["total", "verbal", "material", "goldstein"],
    };
  }, [state.selectedComponents]);

  /**
   * Memoize context value to prevent unnecessary re-renders
   */
  const value = useMemo(
    () => ({
      // State properties
      selectedComponents: state.selectedComponents,
      normalization: state.normalization,
      countryPairs: state.countryPairs,
      chartData: state.chartData,
      loading: state.loading,
      error: state.error,

      aggregationMode: state.aggregationMode,
      selectedCountries: state.selectedCountries,

      // Computed properties
      validationStatus: getValidationStatus,
      normalizationForAPI: getNormalizationForAPI,
      componentStats: getComponentStats,

      // Actions with stable references
      toggleComponent,
      setNormalization,
      addCountryPair,
      removeCountryPair,
      clearCountryPairs,
      setChartData,
      setLoading,
      setError,
      clearError,
      resetChart,

      setAggregationMode,
      addSelectedCountry,
      removeSelectedCountry,
      clearSelectedCountries,
    }),
    [
      // Individual state properties
      state.selectedComponents,
      state.normalization,
      state.countryPairs,
      state.chartData,
      state.loading,
      state.error,

      state.aggregationMode,
      state.selectedCountries,
      // Computed values
      getValidationStatus,
      getNormalizationForAPI,
      getComponentStats,
      // Action functions (stable due to useCallback)
      toggleComponent,
      setNormalization,
      addCountryPair,
      removeCountryPair,
      clearCountryPairs,
      setChartData,
      setLoading,
      setError,
      clearError,
      resetChart,

      setAggregationMode,
      addSelectedCountry,
      removeSelectedCountry,
      clearSelectedCountries,
    ]
  );

  return (
    <ChartContext.Provider value={value}>{children}</ChartContext.Provider>
  );
}

/**
 * Hook to use chart context
 * @returns {Object} Chart context value
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useChartContext() {
  const context = useContext(ChartContext);

  if (!context) {
    throw new Error("useChartContext must be used within a ChartProvider");
  }

  return context;
}
